import { marketApi } from '@/shared/api/services/marketApi';
import { isOk } from '@/shared/api/types';
import type { ContextoAlerta } from '@/shared/domain/alertas';
import type { Alerta, Historico, TasaBcv, UltimosData } from '@/shared/domain/types';
import { fetchQuery } from './useApi';

const STALE_MS = 10 * 60_000;
export const nuevoId = (): string =>
  `local-${globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`}`;

const esPct = (a: Alerta): boolean => a.condicion === 'sube_pct' || a.condicion === 'baja_pct';

/**
 * Arma el contexto de evaluación para un conjunto de alertas: precios y tasa de hoy, más los históricos
 * que hagan falta (solo las alertas por porcentaje los necesitan; se comparten con la caché de consultas).
 */
export async function contextoDeAlertas(
  alertas: readonly Alerta[],
  ultimos: UltimosData | undefined,
  ahora: Date,
): Promise<ContextoAlerta | null> {
  if (!ultimos) return null;
  const pct = alertas.filter((a) => a.activa && esPct(a));
  const rubros = [...new Set(pct.flatMap((a) => (a.filtro ? [a.filtro.rubro] : [])))];
  const quiereBcv = pct.some((a) => a.objetivo === 'bcv');

  const [series, bcv] = await Promise.all([
    Promise.all(
      rubros.map((rubro) =>
        fetchQuery<Historico[]>(
          `historico-series:${rubro}`,
          (signal) => marketApi.getHistoricoSeries({ rubro }, { signal }),
          { staleMs: STALE_MS },
        ),
      ),
    ),
    quiereBcv
      ? fetchQuery<TasaBcv[]>(
          'bcv-historico:',
          (signal) => marketApi.getBcvHistorico(undefined, { signal }),
          { staleMs: STALE_MS },
        )
      : null,
  ]);
  return {
    precios: ultimos.precios,
    tasaBcvActual: ultimos.tasaBcvActual,
    tasaBcvFecha: ultimos.tasaBcvFecha,
    historicos: series.flatMap((r) => (isOk(r) ? r.data : [])),
    bcvHistorico: bcv && isOk(bcv) ? bcv.data : [],
    ahora,
  };
}
